import React, { useEffect, useRef, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import lodash from 'lodash';
import cx from 'classnames';

import { getAllRolesThunk, saveMyOrganization, updateUserList } from '../../store/user/userSlice';
import { getUserListPagePaginationLimit, setUserListPagePaginationLimit } from '../../helpers';
import { getOrganizationUsers, getUserOrganization } from '../../api/organizationsApi';
import { OrganizationUser, OrganizationUsersHeader } from '../modules/Users';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { ContainedButton, OutlinedButton, RolesButton } from '../modules/Buttons';
import { PaginationFooter } from '../modules/PaginationFooter';
import BellIcon from '../../assets/icons/bell-button.svg';
import CreateSuperAdminModal from './CreateSuperAdminModal';
import { ROLES, RolesLowerCase } from '../../enums/ROLES';
import { SortFields, SortType } from '../../enums/SORT';
import LinearProgress from '../modules/LinearProgress';
import useDebounce from '../../helpers/useDebounce';
import { User } from '../modules/OrganizationCard';
import SearchInput from '../modules/SearchInput';
import CreateUserModal from './CreateUserModal';
import styles from './UsersList.module.scss';
import Divider from '../modules/Divider';
import { patchBulkDownloadPreferencesByRole, postResendRegistrationLink } from '../../api';
import { USER } from '../../enums';
import RenotifyUserModal from './RenotifyUserModal';
import { enqueueSnackbar } from 'notistack';
import { Tooltip } from '@mui/material';
import ImportUsersModal from './ImportUsersModal';
import Modal from '../modules/Modal';
import RolePlate from '../modules/RolePlate';
import { useDimensions } from '../../helpers';
import ApplyPreferencesModal from '../modules/ApplyPreferencesModal';
import { PermissionSelector } from '../modules/PermissionSelector';

enum ROLE_OPTIONS {
  ALL = 'All',
}

type ROLES_SELECTOR = ROLES | ROLE_OPTIONS;
type DOWNLOAD_ROLE = RolesLowerCase.ANALYST | RolesLowerCase.VIEWER;

const UsersList = () => {
  const dispatch = useAppDispatch();
  const { widthD } = useDimensions();
  const isMobile = widthD < 768;
  const [showAddModal, setShowAddModal] = useState(false);
  const switchModal = () => {
    setShowAddModal(!showAddModal);
  };

  const [currentRole, setCurrentRole] = useState<ROLE_OPTIONS | ROLES>(ROLE_OPTIONS.ALL);
  const [searchText, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState<SortFields>(SortFields.NAME);
  const [sortType, setSortType] = useState<SortType>(SortType.ASC);
  const [total, setTotal] = useState<number>(0);

  const { role: userRole, userList, myOrganization } = useAppSelector((state) => state.user);
  const debouncedValue = useDebounce<string>(searchText, 500);

  useEffect(() => {
    const prefferedLimit = getUserListPagePaginationLimit();
    if (prefferedLimit && lodash.isNumber(+prefferedLimit)) {
      setRowsPerPage(+prefferedLimit);
    }
  }, []);

  const fetchData = async () => {
    setLoading(true);

    const rolesQuery =
      currentRole === ROLE_OPTIONS.ALL
        ? `${RolesLowerCase.ORGANIZATION_ADMIN}&role=${RolesLowerCase.ANALYST}&role=${RolesLowerCase.VIEWER}${
            userRole === ROLES.SUPER_ADMIN ? `&role=${RolesLowerCase.SUPER_ADMIN}` : ''
          }`
        : RolesLowerCase[currentRole];

    try {
      const org = await getUserOrganization();
      if (org.data) {
        dispatch(saveMyOrganization(org.data));
      }

      const res = await getOrganizationUsers({
        role: rolesQuery,
        page: page,
        limit: rowsPerPage,
        search: searchText.trim(),
        sort: sortType,
        sortField,
      });

      const users = res.data.documents as User[];
      dispatch(updateUserList(users));
      setTotal(res.data.total);
      setLoading(false);

      return res;
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    dispatch(getAllRolesThunk());
  }, []);

  useEffect(() => {
    fetchData();
  }, [debouncedValue, currentRole, sortType, page, rowsPerPage]);

  const setRoleFilter = (role: ROLES_SELECTOR) => {
    setPage(0);
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
    setCurrentRole(role);
  };

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    onPaginate(true, 0);
    setSearch(e.target.value);
  };

  const sortByType = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    const target = e.currentTarget as HTMLButtonElement;
    if (target.name) {
      setSortField(target.name as SortFields);
      setSortType(sortType === SortType.ASC ? SortType.DESC : SortType.ASC);
    }
  };

  const onPaginate = (_: any, newPage: number) => {
    setPage(newPage);
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  };

  const listRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const toolbarLeftRef = useRef<HTMLDivElement>(null);
  const toolbarRolesRef = useRef<HTMLDivElement>(null);
  const toolbarRightRef = useRef<HTMLDivElement>(null);
  const [showRoleDropdown, setShowRoleDropdown] = useState(isMobile);
  const [stackToolbarActions, setStackToolbarActions] = useState(isMobile);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      setShowRoleDropdown(isMobile);
      return;
    }

    const updateRoleLayout = () => {
      if (!toolbarRef.current || !toolbarLeftRef.current || !toolbarRolesRef.current || !toolbarRightRef.current) return;

      const childrenWidth = (element: HTMLElement) => {
        const children = Array.from(element.children) as HTMLElement[];
        const gap = parseFloat(getComputedStyle(element).columnGap) || 0;

        return children.reduce((width, child) => {
          const style = getComputedStyle(child);
          return width + child.offsetWidth + parseFloat(style.marginLeft) + parseFloat(style.marginRight);
        }, gap * Math.max(children.length - 1, 0));
      };
      const searchWidth = (toolbarLeftRef.current.firstElementChild as HTMLElement)?.offsetWidth || 0;
      const actionsWidth = childrenWidth(toolbarRightRef.current);
      const requiredWidth = searchWidth + childrenWidth(toolbarRolesRef.current) + actionsWidth + 32;
      setShowRoleDropdown(isMobile || toolbarRef.current.clientWidth < requiredWidth);
      setStackToolbarActions(isMobile || toolbarRef.current.clientWidth < searchWidth + actionsWidth + 160);
    };

    const observer = new ResizeObserver(updateRoleLayout);
    [toolbarRef.current, toolbarRolesRef.current, toolbarRightRef.current].forEach((element) => {
      if (element) observer.observe(element);
    });
    updateRoleLayout();

    return () => observer.disconnect();
  }, [isMobile, userRole]);

  const onRowsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setUserListPagePaginationLimit(parseInt(e.target.value, 10));
    setPage(0);
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  };

  const [showAddSuperAdminModal, setShowSuperAdminModal] = useState(false);
  const switchAddSuperAdminModal = () => {
    setShowSuperAdminModal(!showAddSuperAdminModal);
  };

  const [renotifyUsers, setRenotifyUsers] = useState<USER[] | 'all' | null>(null);
  const toRenotifyUserModal = (val: USER[] | 'all' | null) => {
    setRenotifyUsers(val);
  };

  const closeRenotifyModal = () => {
    setRenotifyUsers(null);
  };

  const renotifyAll = async () => {
    setLoading(true);
    try {
      const res = await postResendRegistrationLink({ all: true });
      if (res?.data?.emailsSentTo) {
        enqueueSnackbar(`Links are sent to ${res.data.emailsSentTo} user(s)`, {
          preventDuplicate: true,
          variant: 'success',
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
      } else {
        enqueueSnackbar(`0 emails was sent`, {
          preventDuplicate: true,
          variant: 'warning',
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
      }

      localStorage.setItem('last_resend_oa', `${new Date()}`);
      await fetchData();
    } catch (err) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const lastResendAllTime = localStorage.getItem('last_resend_oa');
  const [disableSendAll, setDisableSendAll] = useState(false);

  useEffect(() => {
    if (lastResendAllTime) {
      const lastResend = new Date(localStorage.getItem('last_resend_oa')!);
      const isWithin24Hours = Date.now() - lastResend.getTime() < 24 * 60 * 60 * 1000;
      if (isWithin24Hours) {
        setDisableSendAll(true);
      }
    }
  }, [lastResendAllTime]);

  const [showImportModal, setShowImportModal] = useState(false);
  const switchImportModal = () => {
    setShowImportModal(!showImportModal);
  };

  const [showDownloadPreferencesModal, setShowDownloadPreferencesModal] = useState(false);
  const [showOrgPreferencesModal, setShowOrgPreferencesModal] = useState(false);
  const [showDownloadPreferencesConfirmModal, setShowDownloadPreferencesConfirmModal] = useState(false);
  const [downloadPreferencesUpdateLoading, setDownloadPreferencesUpdateLoading] = useState(false);
  const [pendingDownloadPreferencesUpdate, setPendingDownloadPreferencesUpdate] = useState<{
    role: DOWNLOAD_ROLE;
    allowDownload: boolean;
  } | null>(null);

  const switchDownloadPreferencesModal = () => {
    if (downloadPreferencesUpdateLoading) return;
    setShowDownloadPreferencesModal((prev) => !prev);
  };

  const closeDownloadPreferencesConfirmModal = () => {
    if (downloadPreferencesUpdateLoading) return;
    setShowDownloadPreferencesConfirmModal(false);
    setPendingDownloadPreferencesUpdate(null);
  };

  const onDownloadPreferencesChoice = (role: DOWNLOAD_ROLE, allowDownload: boolean) => {
    setPendingDownloadPreferencesUpdate({ role, allowDownload });
    setShowDownloadPreferencesModal(false);
    setShowDownloadPreferencesConfirmModal(true);
  };

  const updateDownloadPreferencesByRole = async () => {
    if (!pendingDownloadPreferencesUpdate || downloadPreferencesUpdateLoading) return;
    try {
      setDownloadPreferencesUpdateLoading(true);
      await patchBulkDownloadPreferencesByRole({
        role: pendingDownloadPreferencesUpdate.role,
        allow_download_own_composers: pendingDownloadPreferencesUpdate.allowDownload,
      });
      enqueueSnackbar('Download Composer preferences updated', {
        preventDuplicate: true,
        variant: 'success',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      await fetchData();
      setShowDownloadPreferencesConfirmModal(false);
      setPendingDownloadPreferencesUpdate(null);
    } catch (err) {
      // @ts-ignore
      const errMessage = err?.response?.data?.message || 'Failed to update Download Composer preferences';
      enqueueSnackbar(errMessage, {
        preventDuplicate: true,
        variant: 'warning',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
    } finally {
      setDownloadPreferencesUpdateLoading(false);
    }
  };

  const pendingDownloadRoleLabel = pendingDownloadPreferencesUpdate?.role === RolesLowerCase.ANALYST ? 'Analysts' : 'Viewers';
  const pendingDownloadAbilityLabel = pendingDownloadPreferencesUpdate?.allowDownload ? 'able' : 'unable';
  const roleFilterOptions = [
    { label: 'All', value: ROLE_OPTIONS.ALL },
    ...(userRole === ROLES.SUPER_ADMIN ? [{ label: 'Super Admin', value: ROLES.SUPER_ADMIN }] : []),
    { label: 'OA', value: ROLES.ORGANIZATION_ADMIN },
    { label: 'Analyst', value: ROLES.ANALYST },
    { label: 'Viewer', value: ROLES.VIEWER },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.link}>
        <div className={styles.linkEl}>Users List</div>
      </div>
      <Divider />
      {loading && <LinearProgress />}
      <div className="mb-5">
        <div className={styles.pageTitle}>Users List</div>
        <div className={cx(styles.toolbar, stackToolbarActions && styles.toolbarStacked)} ref={toolbarRef}>
          <div className={styles.toolbarLeft} ref={toolbarLeftRef}>
            <SearchInput className={styles.search} onChange={onSearch} />
            <div
              className={cx(styles.toolbarRoles, showRoleDropdown && styles.toolbarRolesHidden)}
              ref={toolbarRolesRef}
            >
              <RolesButton
                kind={currentRole === ROLE_OPTIONS.ALL ? 'white' : 'gray'}
                hover={currentRole !== ROLE_OPTIONS.ALL ? 'white' : 'none'}
                onClick={() => setRoleFilter(ROLE_OPTIONS.ALL)}
                customStyles={{ borderRadius: 'var(--ui-border-radius)' }}
              >
                <div className={styles.roleButton}>All {currentRole === ROLE_OPTIONS.ALL && <CheckIcon sx={{ height: '12px' }} />}</div>
              </RolesButton>
              {userRole === ROLES.SUPER_ADMIN && (
                <RolesButton
                  kind={currentRole === ROLES.SUPER_ADMIN ? 'mint' : 'gray'}
                  hover={currentRole !== ROLES.SUPER_ADMIN ? 'mint' : 'none'}
                  onClick={() => setRoleFilter(ROLES.SUPER_ADMIN)}
                  customStyles={{ borderRadius: 'var(--ui-border-radius)', marginLeft: '8px' }}
                >
                  <div className={styles.roleButton}>
                    Super Admin {currentRole === ROLES.SUPER_ADMIN && <CheckIcon sx={{ height: '12px' }} />}
                  </div>
                </RolesButton>
              )}

              <RolesButton
                kind={currentRole === ROLES.ORGANIZATION_ADMIN ? 'orange' : 'gray'}
                hover={currentRole !== ROLES.ORGANIZATION_ADMIN ? 'orange' : 'none'}
                onClick={() => setRoleFilter(ROLES.ORGANIZATION_ADMIN)}
                customStyles={{ borderRadius: 'var(--ui-border-radius)', marginLeft: '8px' }}
              >
                <div className={styles.roleButton}>
                  OA {currentRole === ROLES.ORGANIZATION_ADMIN && <CheckIcon sx={{ height: '12px' }} />}
                </div>
              </RolesButton>
              <RolesButton
                kind={currentRole === ROLES.ANALYST ? 'blue' : 'gray'}
                hover={currentRole !== ROLES.ANALYST ? 'blue' : 'none'}
                onClick={() => setRoleFilter(ROLES.ANALYST)}
                customStyles={{ borderRadius: 'var(--ui-border-radius)', marginLeft: '8px' }}
              >
                <div className={styles.roleButton}>Analyst {currentRole === ROLES.ANALYST && <CheckIcon sx={{ height: '12px' }} />}</div>
              </RolesButton>
              <RolesButton
                kind={currentRole === ROLES.VIEWER ? 'green' : 'gray'}
                hover={currentRole !== ROLES.VIEWER ? 'green' : 'none'}
                onClick={() => setRoleFilter(ROLES.VIEWER)}
                customStyles={{ borderRadius: 'var(--ui-border-radius)', marginLeft: '8px' }}
              >
                <div className={styles.roleButton}>Viewer {currentRole === ROLES.VIEWER && <CheckIcon sx={{ height: '12px' }} />}</div>
              </RolesButton>
            </div>
            {showRoleDropdown && (
              <PermissionSelector
                title="Role"
                selectedValues={[
                  roleFilterOptions.find((option) => option.value === currentRole) || roleFilterOptions[0],
                ]}
                options={roleFilterOptions}
                className={styles.roleDropdown}
                onSelect={(values) => setRoleFilter(values[0].value as ROLES_SELECTOR)}
                singleValueSelector
                withoutCheckBox
                style={{ top: '42px', bottom: 'auto', left: '0', right: 'auto', width: '150px' }}
              />
            )}
          </div>

          <div className={styles.toolbarRight} ref={toolbarRightRef}>
            {userRole === ROLES.ORGANIZATION_ADMIN && (
              <ContainedButton className={styles.importUserIcon} onClick={switchImportModal}>
                {isMobile ? 'Import Users' : '+ Import Users'}
              </ContainedButton>
            )}
            {userRole === ROLES.ORGANIZATION_ADMIN && (
              <ContainedButton className={styles.changeDownloadPreferencesIcon} onClick={switchDownloadPreferencesModal}>
                Change Download Preferences
              </ContainedButton>
            )}
            {userRole === ROLES.ORGANIZATION_ADMIN && (
              <ContainedButton className={styles.changeDownloadPreferencesIcon} onClick={() => setShowOrgPreferencesModal(true)}>
                Change Preferences
              </ContainedButton>
            )}
            <Tooltip title={'You can send registration link once per 24 hours'}>
              <div>
                <ContainedButton disabled={disableSendAll} className={styles.renotifyAll} onClick={renotifyAll}>
                  Re-notify All
                </ContainedButton>
              </div>
            </Tooltip>
            {userRole === ROLES.ORGANIZATION_ADMIN && (
              <ContainedButton className={styles.addUserIcon} onClick={switchModal}>
                {isMobile ? 'Add User' : '+ Add User'}
              </ContainedButton>
            )}
            {userRole === ROLES.SUPER_ADMIN && <ContainedButton onClick={switchAddSuperAdminModal}>+ Add Super Admin</ContainedButton>}
          </div>
        </div>
        <div className={styles.usersListHeader}>
          <OrganizationUsersHeader sortByType={sortByType} sortField={sortField} sortType={sortType} />
        </div>
        <div className={styles.usersList} ref={listRef}>
          {userList.map((userObj) => (
            <div className="mb-5" key={userObj.user.id}>
              <OrganizationUser
                toRenotifyUserModal={toRenotifyUserModal}
                user={{ ...userObj.user, role: { ...userObj.role } }}
                fetchData={fetchData}
              />
            </div>
          ))}
        </div>
        <PaginationFooter
          page={page}
          total={total}
          onPageChange={onPaginate}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsChange}
        />
      </div>
      <CreateUserModal
        existEmails={userList.map((userObj) => userObj.user.email)}
        fetchData={fetchData}
        open={showAddModal}
        switchModal={switchModal}
      />
      {userRole === ROLES.SUPER_ADMIN && (
        <CreateSuperAdminModal fetchData={fetchData} open={showAddSuperAdminModal} switchModal={switchAddSuperAdminModal} />
      )}
      <RenotifyUserModal renotifyUsers={renotifyUsers} open={!!renotifyUsers} switchModal={closeRenotifyModal} fetchData={fetchData} />
      {myOrganization?.id && <ImportUsersModal open={showImportModal} switchModal={switchImportModal} fetchData={fetchData} />}

      <Modal open={showDownloadPreferencesModal} onClose={switchDownloadPreferencesModal}>
        <div className={styles.downloadPreferencesModalBody}>
          <div className={styles.modalEyebrow}>Download Composer</div>
          <div className={styles.modalTitle}>Force Download Composer access by user type</div>
          <div className={styles.modalDescription}>Apply this change to all existing users in the selected role.</div>
          <div className={styles.preferenceRows}>
            <div className={styles.preferenceRow}>
              <div className={styles.preferenceRole}>
                <RolePlate userRole={RolesLowerCase.ANALYST} className={styles.rolePlate} />
              </div>
              <div
                className={cx(styles.preferenceBtn, styles.grn)}
                onClick={() => onDownloadPreferencesChoice(RolesLowerCase.ANALYST, true)}
              >
                Force On
              </div>
              <div
                className={cx(styles.preferenceBtn, styles.red)}
                onClick={() => onDownloadPreferencesChoice(RolesLowerCase.ANALYST, false)}
              >
                Force Off
              </div>
            </div>

            <div className={styles.preferenceRow}>
              <div className={styles.preferenceRole}>
                <RolePlate userRole={RolesLowerCase.VIEWER} className={styles.rolePlate} />
              </div>
              <div
                className={cx(styles.preferenceBtn, styles.grn)}
                onClick={() => onDownloadPreferencesChoice(RolesLowerCase.VIEWER, true)}
              >
                Force On
              </div>
              <div
                className={cx(styles.preferenceBtn, styles.red)}
                onClick={() => onDownloadPreferencesChoice(RolesLowerCase.VIEWER, false)}
              >
                Force Off
              </div>
            </div>
          </div>

          <div className={styles.modalFooter}>
            <OutlinedButton kind="default" onClick={switchDownloadPreferencesModal}>
              Cancel
            </OutlinedButton>
          </div>
        </div>
      </Modal>

      <Modal
        open={showDownloadPreferencesConfirmModal}
        onClose={closeDownloadPreferencesConfirmModal}
        loading={downloadPreferencesUpdateLoading}
      >
        <div className={styles.downloadPreferencesModalBody} style={{ width: '360px' }}>
          <div className={cx(styles.modalTitle, styles.confirmTitle)}>
            Are you sure you want to change all existing {pendingDownloadRoleLabel} so that they are {pendingDownloadAbilityLabel} to
            download their own Online Composers?
          </div>
          <div className={styles.confirmActions}>
            <OutlinedButton kind="default" onClick={updateDownloadPreferencesByRole} disabled={downloadPreferencesUpdateLoading}>
              Yes, I am sure
            </OutlinedButton>
            <ContainedButton kind="purple" onClick={closeDownloadPreferencesConfirmModal} disabled={downloadPreferencesUpdateLoading}>
              No, cancel
            </ContainedButton>
          </div>
        </div>
      </Modal>
      <ApplyPreferencesModal
        open={showOrgPreferencesModal}
        onClose={() => setShowOrgPreferencesModal(false)}
        title="Organisation Preferences"
        allowOrgAndRole
      />
    </div>
  );
};

export default UsersList;
